import React, { useCallback, useEffect, useRef, useState } from "react";
import { Power, Volume2, VolumeX, Volume1, Plus, Minus, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Play, Pause, Square, Rewind, FastForward, Tv, MonitorPlay, RefreshCw, Loader2, Hash, Home, CornerUpLeft, Menu, LogOut } from "lucide-react";
import { toast } from "../hooks/use-toast";
import { tr } from "../lib/i18n";
import { R } from "../lib/roomI18n";

const APPS_FALLBACK = [{ id: "netflix", label: "Netflix" }, { id: "youtube", label: "YouTube" }, { id: "prime", label: "Prime" }, { id: "disney", label: "Disney+" }, { id: "dazn", label: "DAZN" }];
const INPUT_LABEL = { digitalTv: "TV", dtv: "TV", HDMI1: "HDMI 1", HDMI2: "HDMI 2", HDMI3: "HDMI 3", HDMI4: "HDMI 4", USB: "USB", AV: "AV", COMPONENT: "Comp." };

const Key = ({ onClick, disabled, active, children, className = "", testId, title, wide }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    data-testid={testId}
    className={`select-none inline-flex items-center justify-center gap-1 rounded-full border text-xs font-semibold transition-[transform,background-color,box-shadow] duration-150 active:scale-95 disabled:opacity-40 ${wide ? "px-4 h-10" : "h-10 w-10"} ${
      active ? "bg-[#C48B31] border-[#C48B31] text-[#2b1410] shadow-[0_0_0_3px_rgba(196,139,49,0.25)]" : "bg-[#5a251f] border-[#6d3029] text-[#f7ebdf] hover:bg-[#6d3029]"
    } ${className}`}
  >
    {children}
  </button>
);

/* Samsung-style circular navigation ring: 4 arc segments + centre OK */
function NavRing({ slug, disabled, send, t }) {
  const seg = "absolute flex items-center justify-center text-[#f7ebdf] transition-[background-color,transform] duration-150 active:scale-95 disabled:opacity-40 hover:bg-[#6d3029]/80";
  const dirs = [
    { k: "UP", cls: "top-0 left-1/2 -translate-x-1/2 h-[30%] w-[44%] rounded-t-full pt-2 items-start", Icon: ChevronUp },
    { k: "DOWN", cls: "bottom-0 left-1/2 -translate-x-1/2 h-[30%] w-[44%] rounded-b-full pb-2 items-end", Icon: ChevronDown },
    { k: "LEFT", cls: "left-0 top-1/2 -translate-y-1/2 w-[30%] h-[44%] rounded-l-full pl-2 justify-start", Icon: ChevronLeft },
    { k: "RIGHT", cls: "right-0 top-1/2 -translate-y-1/2 w-[30%] h-[44%] rounded-r-full pr-2 justify-end", Icon: ChevronRight },
  ];
  return (
    <div className="relative h-44 w-44 rounded-full bg-[#5a251f] border border-[#6d3029] shadow-[inset_0_2px_10px_rgba(0,0,0,0.35)]" data-testid={`tv-remote-${slug}-ring`}>
      {dirs.map(({ k, cls, Icon }) => (
        <button key={k} type="button" disabled={disabled} onClick={() => send("key", k)} aria-label={k} data-testid={`tv-remote-${slug}-key-${k}`} className={`${seg} ${cls}`}>
          <Icon className="h-5 w-5" />
        </button>
      ))}
      <button
        type="button"
        disabled={disabled}
        onClick={() => send("key", "OK")}
        data-testid={`tv-remote-${slug}-key-OK`}
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[42%] w-[42%] rounded-full bg-[#2b1410] border-2 border-[#C48B31] text-[#e6c46a] text-sm font-bold tracking-wide shadow-[0_2px_10px_rgba(0,0,0,0.45)] active:scale-95 transition-transform disabled:opacity-40"
      >
        OK
      </button>
    </div>
  );
}

export default function TvRemote({ slug, kind = "frame", compact = false, getStatus, sendCommand, className = "", lang = "it" }) {
  const t = (k) => tr(R[k], lang);
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState("");

  const getRef = useRef(getStatus); getRef.current = getStatus;
  const refresh = useCallback(async () => {
    try { setSt(await getRef.current()); }
    catch (e) { setSt((s) => ({ ...(s || {}), error: e?.response?.data?.detail || e.message })); }
    finally { setLoading(false); }
  }, [slug]);
  useEffect(() => { refresh(); }, [refresh]);

  const send = async (action, value) => {
    setBusy(action);
    try {
      const data = await sendCommand(action, value == null ? undefined : String(value));
      if (data?.status) setSt(data.status);
    } catch (e) {
      toast({ title: t("cmdFailed"), description: e?.response?.data?.detail || e.message });
    } finally { setBusy(null); }
  };

  const on = st?.switch === "on";
  const muted = st?.mute === "muted";
  const vol = typeof st?.volume === "number" ? st.volume : null;
  const inputs = (st?.inputs || []).filter((i) => INPUT_LABEL[i]);
  const B = (a) => busy === a;
  const off = busy || !on;
  const small = "!h-9 !w-9";

  return (
    <div className={`rounded-[28px] bg-[#4a1c17] text-[#f7ebdf] p-4 shadow-inner ${className}`} data-testid={`tv-remote-${slug}`}>
      {/* Top row: power + status + refresh */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <button
          type="button"
          onClick={() => send(on ? "power_off" : "power_on")}
          disabled={!!busy}
          title={on ? t("tvTurnOff") : t("tvTurnOn")}
          aria-label={on ? t("tvTurnOff") : t("tvTurnOn")}
          data-testid={`tv-remote-${slug}-power`}
          className={`h-12 w-12 rounded-full border-2 inline-flex items-center justify-center transition-[background-color,box-shadow,transform] duration-150 active:scale-95 disabled:opacity-50 ${on ? "bg-[#C48B31] border-[#C48B31] text-[#2b1410] shadow-[0_0_0_4px_rgba(196,139,49,0.25)]" : "bg-[#2b1410] border-[#6d3029] text-rose-300"}`}
        >
          {B("power_on") || B("power_off") ? <Loader2 className="h-5 w-5 animate-spin" /> : <Power className="h-5 w-5" />}
        </button>
        <div className="flex-1 min-w-0 text-center">
          <div className="flex items-center justify-center gap-1.5">
            <span className={`h-2 w-2 rounded-full shrink-0 ${loading ? "bg-stone-400 animate-pulse" : st?.health === "OFFLINE" ? "bg-rose-500" : on ? "bg-emerald-400" : "bg-stone-500"}`} />
            <span className="text-[11px] tracking-wide uppercase text-[#e6c46a] truncate" data-testid={`tv-remote-${slug}-state`}>
              {loading ? t("tvReading") : st?.error ? t("tvUnreachable") : st?.health === "OFFLINE" ? "offline" : on ? `${t("tvOn")} · ${INPUT_LABEL[st?.input] || st?.input || ""}${st?.channel_name ? ` · ${st.channel_name}` : st?.channel ? ` · Ch ${st.channel}` : ""}` : t("tvOff")}
            </span>
          </div>
          <div className="text-[10px] text-[#f7ebdf]/50 mt-0.5">{t("tvPower")}</div>
        </div>
        <button type="button" onClick={refresh} className="h-9 w-9 inline-flex items-center justify-center rounded-full text-[#e6c46a]/80 hover:text-[#e6c46a] hover:bg-[#5a251f]" title="Refresh" data-testid={`tv-remote-${slug}-refresh`}><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
      </div>

      {/* Ring + side keys */}
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 mt-2">
        <div className="flex flex-col gap-3">
          <Key onClick={() => send("key", "HOME")} disabled={off} testId={`tv-remote-${slug}-key-HOME`} title="Home" className={small}><Home className="h-4 w-4" /></Key>
          <Key onClick={() => send("key", "MENU")} disabled={off} testId={`tv-remote-${slug}-key-MENU`} title="Menu" className={small}><Menu className="h-4 w-4" /></Key>
        </div>
        <div className="flex justify-center"><NavRing slug={slug} disabled={off} send={send} t={t} /></div>
        <div className="flex flex-col gap-3">
          <Key onClick={() => send("key", "BACK")} disabled={off} testId={`tv-remote-${slug}-key-BACK`} title="Back" className={small}><CornerUpLeft className="h-4 w-4" /></Key>
          <Key onClick={() => send("key", "EXIT")} disabled={off} testId={`tv-remote-${slug}-key-EXIT`} title="Exit" className={small}><LogOut className="h-4 w-4" /></Key>
        </div>
      </div>

      {/* Volume + Channel pill rockers (Samsung style) */}
      <div className="grid grid-cols-2 gap-4 mt-4 px-2">
        <div className="rounded-full bg-[#3a1610] border border-[#6d3029] py-2 flex flex-col items-center gap-1" data-testid={`tv-remote-${slug}-vol-rocker`}>
          <Key onClick={() => send("volume_up")} disabled={off} testId={`tv-remote-${slug}-vol-up`} title="Vol +" className="!bg-transparent !border-transparent"><Plus className="h-5 w-5" /></Key>
          <button type="button" onClick={() => send(muted ? "unmute" : "mute")} disabled={off} data-testid={`tv-remote-${slug}-mute`} className={`flex flex-col items-center leading-none py-0.5 ${muted ? "text-rose-300" : "text-[#e6c46a]"} disabled:opacity-40`} title="Mute">
            {muted ? <VolumeX className="h-4 w-4" /> : vol != null && vol < 10 ? <Volume1 className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            <span className="text-[10px] mt-0.5 tabular-nums" data-testid={`tv-remote-${slug}-vol`}>{muted ? "mute" : vol != null ? vol : t("tvVolume")}</span>
          </button>
          <Key onClick={() => send("volume_down")} disabled={off} testId={`tv-remote-${slug}-vol-down`} title="Vol −" className="!bg-transparent !border-transparent"><Minus className="h-5 w-5" /></Key>
        </div>
        <div className="rounded-full bg-[#3a1610] border border-[#6d3029] py-2 flex flex-col items-center gap-1" data-testid={`tv-remote-${slug}-ch-rocker`}>
          <Key onClick={() => send("channel_up")} disabled={off} testId={`tv-remote-${slug}-ch-up`} title="Ch +" className="!bg-transparent !border-transparent"><ChevronUp className="h-5 w-5" /></Key>
          <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); if (channel) { send("set_channel", channel); setChannel(""); } }}>
            <Hash className="h-3 w-3 text-[#e6c46a]" />
            <input value={channel} onChange={(e) => setChannel(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" placeholder={t("tvChannel")} disabled={!on} data-testid={`tv-remote-${slug}-ch-input`} className="w-14 bg-transparent text-center text-xs text-[#f7ebdf] placeholder:text-[#f7ebdf]/40 outline-none border-b border-[#e6c46a]/40 focus:border-[#e6c46a] disabled:opacity-40" />
          </form>
          <Key onClick={() => send("channel_down")} disabled={off} testId={`tv-remote-${slug}-ch-down`} title="Ch −" className="!bg-transparent !border-transparent"><ChevronDown className="h-5 w-5" /></Key>
        </div>
      </div>

      {/* Playback + inputs */}
      <div className="flex items-center justify-center gap-1.5 mt-4 flex-wrap">
        <Key onClick={() => send("key", "REWIND")} disabled={off} testId={`tv-remote-${slug}-rew`} title="Rewind" className="!h-8 !w-8"><Rewind className="h-3.5 w-3.5" /></Key>
        <Key onClick={() => send("play")} disabled={off} testId={`tv-remote-${slug}-play`} title="Play" className="!h-8 !w-8"><Play className="h-3.5 w-3.5" /></Key>
        <Key onClick={() => send("pause")} disabled={off} testId={`tv-remote-${slug}-pause`} title="Pause" className="!h-8 !w-8"><Pause className="h-3.5 w-3.5" /></Key>
        <Key onClick={() => send("stop")} disabled={off} testId={`tv-remote-${slug}-stop`} title="Stop" className="!h-8 !w-8"><Square className="h-3.5 w-3.5" /></Key>
        <Key onClick={() => send("key", "FF")} disabled={off} testId={`tv-remote-${slug}-ff`} title="Forward" className="!h-8 !w-8"><FastForward className="h-3.5 w-3.5" /></Key>
        <span className="w-px h-6 bg-[#6d3029] mx-1" />
        {(inputs.length ? inputs : ["dtv", "HDMI1", "HDMI2"]).map((i) => (
          <Key key={i} wide onClick={() => send("set_input", i)} disabled={off} active={st?.input === i} testId={`tv-remote-${slug}-input-${i}`} className="!h-8 !px-3 text-[11px]">{i === "digitalTv" || i === "dtv" ? <Tv className="h-3.5 w-3.5" /> : <MonitorPlay className="h-3.5 w-3.5" />} {INPUT_LABEL[i]}</Key>
        ))}
      </div>

      {/* Apps */}
      {!compact && (
        <div className="mt-4 grid grid-cols-5 gap-1.5">
          {(st?.apps || APPS_FALLBACK).map((a) => (
            <button key={a.id} type="button" onClick={() => send("launch_app", a.id)} disabled={!!busy} data-testid={`tv-remote-${slug}-app-${a.id}`} className="rounded-xl bg-[#f7ebdf] text-[#2b1410] text-[11px] font-bold py-2 px-1 truncate hover:bg-[#e6c46a] active:scale-95 transition-[transform,background-color] duration-150 disabled:opacity-40">
              {B("launch_app") ? "…" : a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
